import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import * as Styled from './Swatch.styled';
import Icon from '../Icons/Icon';
import { ContainerQueryContext } from '../../util/ContainerQuery.context';

const Swatch = ({
  disabled,
  fill,
  hasMargin,
  isSelected,
  label,
  name,
  onChange,
  showLabel,
  size,
  type,
  value,
  isColorFilterSwatch,
  labelDataLabel,
  responsiveSize,
  ...inputProps
}) => {
  const { styleQueryMode } = useContext(ContainerQueryContext);

  return (
    <Styled.Label
      inputType={type}
      aria-label={`${label || value} color swatch`}
    >
      <Styled.Input
        type={type}
        checked={isSelected}
        name={name}
        value={value}
        onChange={onChange}
        readOnly={!onChange}
        disabled={disabled}
        data-testid="optionSwatchLabel"
        {...inputProps}
      />
      <Styled.Container data-label={labelDataLabel}>
        <Styled.Swatch
          disabled={disabled}
          fill={fill}
          hasMargin={hasMargin}
          isSelected={isSelected}
          size={size}
          type={type}
          label={label}
          isColorFilterSwatch={isColorFilterSwatch}
          $responsiveSize={responsiveSize}
          $styleQueryMode={styleQueryMode}
        >
          {isSelected && type === 'checkbox' && (
            <Icon name="check" size="mega" />
          )}
        </Styled.Swatch>
        <Styled.Name
          disabled={disabled}
          isHidden={!showLabel}
          data-label={labelDataLabel}
        >
          {label || value}
        </Styled.Name>
      </Styled.Container>
    </Styled.Label>
  );
};

Swatch.propTypes = {
  /**
   * The value of the Swatch input.
   */
  value: PropTypes.string.isRequired,
  /**
   * name attribute of the `input` element
   */
  name: PropTypes.string,
  /**
   * fill can be a hex code or an image url string
   */
  fill: PropTypes.string.isRequired,
  /**
   * Whether to add CSS margin to Swatch
   */
  hasMargin: PropTypes.bool,
  /**
   * `true` if the Swatch is selected. If the Swatch is type `radio` only one can be selected at a time.
   * If the Swatch is type `checkbox`, multiple can be selected.
   */
  isSelected: PropTypes.bool,
  /**
   * A function that runs when a change event is triggered
   */
  onChange: PropTypes.func,
  /**
   * @deprecated
   * Size of the Swatch
   * If `responsiveSize` is defined, the `size` value will be ignored. If `responsiveSize` is not defined, the `size` value will be utilized.
   */
  size: PropTypes.number,
  /**
   * responsiveSize of the Swatch.
   * Contains the small, medium and large (and above) size values that are used in the corresponding breakpoints.
   * The `responsiveSize` prop takes priority over the `size` prop
   */
  responsiveSize: PropTypes.shape({
    small: PropTypes.number,
    medium: PropTypes.number,
    large: PropTypes.number
  }),
  /**
   * A Swatch can be type `radio` or `checkbox`, this determines the type of the underlying `input` element
   */
  type: PropTypes.oneOf(['radio', 'checkbox']),
  /**
   * `true` if the Swatch is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The label for the Swatch. If undefined, `value` prop will be used.
   */
  label: PropTypes.string,
  /**
   * The data-label attribute for the label definition
   */
  labelDataLabel: PropTypes.string,
  /**
   * If `true`, the label will be displayed next to the Swatch.
   */
  showLabel: PropTypes.bool,
  /**
   * If `true`, the Swatch is a color filter Swatch
   */
  isColorFilterSwatch: PropTypes.bool
};

Swatch.defaultProps = {
  hasMargin: true,
  isSelected: false,
  size: 31,
  type: 'radio',
  disabled: false,
  showLabel: false,
  label: undefined,
  labelDataLabel: 'swatch_label',
  name: undefined,
  onChange: undefined,
  isColorFilterSwatch: false
};

export default Swatch;
